// frontend/src/pages/ForecastPage.js
import { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import api from '../services/api';
import { trackEvent } from '../services/analytics';
import './ForecastPage.css';

const SEVERITY_STYLES = {
  high: { color: '#ef4444', bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.3)' },
  medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.3)' },
  low: { color: '#64748b', bg: 'rgba(100,116,139,0.04)', border: 'rgba(100,116,139,0.2)' },
};

/* ─── Skeleton ─── */
function ForecastSkeleton() {
  return (
    <div className="fc-skeleton">
      <div className="skel skel-narrative" />
      <div className="fc-stats-grid">
        <div className="skel skel-stat" /><div className="skel skel-stat" />
        <div className="skel skel-stat" /><div className="skel skel-stat" />
      </div>
      <div className="skel skel-chart" />
      <div className="skel skel-whatif" />
    </div>
  );
}

/* ─── Custom tooltip ─── */
function ForecastTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const names = {
    optimistic: 'Best case',
    current_trajectory: 'Current path',
    adjusted_trajectory: 'With actions',
    risk: 'Risk',
  };
  const colors = {
    optimistic: '#34d399',
    current_trajectory: '#60a5fa',
    adjusted_trajectory: '#a78bfa',
    risk: '#f87171',
  };
  return (
    <div className="fc-tooltip">
      <div className="fc-tooltip-label">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="fc-tooltip-row">
          <span className="fc-tooltip-dot" style={{ background: colors[p.dataKey] || p.color }} />
          <span className="fc-tooltip-name">{names[p.dataKey] || p.dataKey}</span>
          <span className="fc-tooltip-val">{p.value.toFixed(2)} ★</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Main Page ─── */
export default function ForecastPage() {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [whatIf, setWhatIf] = useState({});

  const fetchForecast = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      let data;
      if (forceRefresh) {
        data = await api.post('/forecast/refresh', {});
      } else {
        data = await api.get('/forecast');
      }
      setForecast(data);
      trackEvent('forecast_viewed', { from_cache: data.from_cache });
      if (data.what_if_scenarios) {
        const initial = {};
        data.what_if_scenarios.forEach((s) => { initial[s.id] = false; });
        setWhatIf(initial);
      }
    } catch (err) {
      const msg = err?.response?.status === 429
        ? 'Rate limit reached — try again in a few minutes'
        : err.message || 'Failed to load forecast';
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const getChartData = () => {
    if (!forecast?.forecast_points) return [];
    let totalBoost = 0;
    if (forecast.what_if_scenarios) {
      forecast.what_if_scenarios.forEach((s) => {
        if (whatIf[s.id]) totalBoost += s.impact;
      });
    }
    return forecast.forecast_points.map((point) => {
      const scale = point.day_offset / 90;
      const adjusted = Math.min(5.0, point.current_trajectory + totalBoost * scale);
      return {
        ...point,
        adjusted_trajectory: Math.round(adjusted * 100) / 100,
      };
    });
  };

  const toggleWhatIf = (id) => {
    setWhatIf((prev) => ({ ...prev, [id]: !prev[id] }));
    trackEvent('forecast_whatif_toggled', { scenario: id });
  };

  const hasAnyWhatIf = Object.values(whatIf).some(Boolean);
  const chartData = getChartData();

  const getProjectedRating = () => {
    if (!chartData.length) return null;
    const last = chartData[chartData.length - 1];
    return hasAnyWhatIf ? last.adjusted_trajectory : last.current_trajectory;
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Reputation Forecast</h1>
            <p className="page-subtitle">Analysing your reputation data...</p>
          </div>
        </div>
        <ForecastSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Reputation Forecast</h1>
            <p className="page-subtitle">Your 90-day reputation outlook</p>
          </div>
        </div>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: '#ef4444', fontSize: 14 }}>{error}</p>
          <button className="btn btn-primary" onClick={() => fetchForecast()} style={{ marginTop: 12 }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!forecast?.has_enough_data) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Reputation Forecast</h1>
            <p className="page-subtitle">Your 90-day reputation outlook</p>
          </div>
        </div>
        <div className="card fc-empty">
          <div className="fc-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #34d399)" strokeWidth="1.5">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <h3>Not enough data yet</h3>
          <p>{forecast?.message || 'Import at least 3 reviews to unlock your reputation forecast.'}</p>
          <a href="/reviews" className="btn btn-primary" style={{ marginTop: 16 }}>Import Reviews</a>
        </div>
      </div>
    );
  }

  const projectedRating = getProjectedRating();
  const delta = projectedRating ? projectedRating - forecast.current_rating : 0;
  const deltaStr = (delta >= 0 ? '+' : '') + delta.toFixed(2);
  const day90 = forecast.forecast_points[forecast.forecast_points.length - 1];

  return (
    <div className="fc-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reputation Forecast</h1>
          <p className="page-subtitle">
            Your 90-day reputation outlook
            {forecast.from_cache && (
              <span className="fc-cache-badge">cached</span>
            )}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => fetchForecast(true)}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* ── AI Narrative ── */}
      {forecast.narrative && (
        <div className="fc-narrative">
          <div className="fc-narrative-glow" />
          <div className="fc-narrative-content">
            <h2 className="fc-narrative-headline">
              {forecast.narrative.headline}
            </h2>
            <p className="fc-narrative-insight">
              {forecast.narrative.insight}
            </p>
            <div className="fc-narrative-action">
              <span className="fc-action-badge">Top action</span>
              <span>{forecast.narrative.action}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Stats ── */}
      <div className="fc-stats-grid">
        <div className="fc-stat">
          <div className="fc-stat-label">Current rating</div>
          <div className="fc-stat-value">{forecast.current_rating}</div>
          <div className="fc-stat-unit">★ from {forecast.review_count} reviews</div>
        </div>
        <div className="fc-stat fc-stat-accent">
          <div className="fc-stat-label">90-day projection</div>
          <div className="fc-stat-value">{projectedRating}</div>
          <div className={`fc-stat-delta ${delta >= 0 ? 'up' : 'down'}`}>
            {deltaStr} ★
          </div>
        </div>
        <div className="fc-stat">
          <div className="fc-stat-label">Momentum</div>
          <div className="fc-stat-value">
            <span className={`fc-momentum ${forecast.momentum > 0.1 ? 'up' : forecast.momentum < -0.1 ? 'down' : 'flat'}`}>
              {forecast.momentum > 0.1 ? '↑' : forecast.momentum < -0.1 ? '↓' : '→'}
            </span>
            {forecast.momentum > 0.1 ? 'Rising' : forecast.momentum < -0.1 ? 'Falling' : 'Stable'}
          </div>
          <div className="fc-stat-unit">{forecast.velocity} reviews/mo</div>
        </div>
        <div className="fc-stat">
          <div className="fc-stat-label">Response rate</div>
          <div className="fc-stat-value">{forecast.response_rate}%</div>
          <div className="fc-stat-unit">
            {forecast.response_rate >= 80 ? 'Excellent' : forecast.response_rate >= 50 ? 'Good' : 'Needs work'}
          </div>
        </div>
      </div>

      {/* ── Chart ── */}
      <div className="card fc-chart-card">
        <div className="fc-chart-header">
          <h3>Rating trajectory</h3>
          <div className="fc-legend">
            <span className="fc-legend-item"><span className="fc-legend-dot" style={{ background: '#34d399' }} /> Best case</span>
            <span className="fc-legend-item">
              <span className="fc-legend-dot" style={{ background: hasAnyWhatIf ? '#a78bfa' : '#60a5fa' }} />
              {hasAnyWhatIf ? 'With actions' : 'Current path'}
            </span>
            <span className="fc-legend-item"><span className="fc-legend-dot" style={{ background: '#f87171' }} /> Risk</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gOpt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gCur" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.12} />
                <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gAdj" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gRisk" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f87171" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-light)' }} />
            <YAxis
              domain={['dataMin - 0.2', 'dataMax + 0.2']}
              tick={{ fontSize: 11, fill: 'var(--text-light)' }}
              tickFormatter={(v) => v.toFixed(1)}
            />
            <Tooltip content={<ForecastTooltip />} />
            {forecast.competitor_avg && (
              <ReferenceLine
                y={forecast.competitor_avg}
                stroke="#94a3b8"
                strokeDasharray="6 4"
                strokeWidth={1}
                label={{ value: `Competitors ${forecast.competitor_avg}`, position: 'right', fontSize: 10, fill: '#94a3b8' }}
              />
            )}
            <Area type="monotone" dataKey="optimistic" stroke="#34d399" strokeWidth={1.5} fill="url(#gOpt)" dot={false} activeDot={{ r: 4, fill: '#34d399' }} />
            {hasAnyWhatIf ? (
              <Area type="monotone" dataKey="adjusted_trajectory" stroke="#a78bfa" strokeWidth={2.5} fill="url(#gAdj)" dot={{ r: 3, fill: '#a78bfa' }} activeDot={{ r: 5, fill: '#a78bfa' }} />
            ) : (
              <Area type="monotone" dataKey="current_trajectory" stroke="#60a5fa" strokeWidth={2} fill="url(#gCur)" dot={{ r: 3, fill: '#60a5fa' }} activeDot={{ r: 5, fill: '#60a5fa' }} />
            )}
            <Area type="monotone" dataKey="risk" stroke="#f87171" strokeWidth={1.5} fill="url(#gRisk)" dot={false} activeDot={{ r: 4, fill: '#f87171' }} />
          </AreaChart>
        </ResponsiveContainer>
        <div className="fc-chart-range">
          <span>{day90.risk} ★ risk</span>
          <span className="fc-chart-range-sep">—</span>
          <span>{day90.current_trajectory} ★ expected</span>
          <span className="fc-chart-range-sep">—</span>
          <span>{day90.optimistic} ★ best case</span>
        </div>
      </div>

      {/* ── What-If ── */}
      {forecast.what_if_scenarios?.length > 0 && (
        <div className="card fc-whatif-card">
          <div className="fc-whatif-header">
            <div>
              <h3>What if you...</h3>
              <p>Toggle actions to see their predicted impact on your rating</p>
            </div>
            {hasAnyWhatIf && (
              <div className="fc-whatif-result">
                <span className="fc-whatif-result-label">Projected</span>
                <span className="fc-whatif-result-value">{projectedRating} ★</span>
              </div>
            )}
          </div>
          <div className="fc-whatif-list">
            {forecast.what_if_scenarios.map((scenario) => (
              <div
                key={scenario.id}
                className={`fc-whatif-item ${whatIf[scenario.id] ? 'active' : ''}`}
                onClick={() => toggleWhatIf(scenario.id)}
              >
                <div className="fc-toggle">
                  <div className="fc-toggle-track">
                    <div className="fc-toggle-knob" />
                  </div>
                </div>
                <div className="fc-whatif-content">
                  <div className="fc-whatif-label">{scenario.label}</div>
                  <div className="fc-whatif-desc">{scenario.description}</div>
                </div>
                <div className={`fc-whatif-impact ${whatIf[scenario.id] ? 'active' : ''}`}>
                  +{scenario.impact} ★
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Risks ── */}
      {forecast.risk_factors?.length > 0 && (
        <div className="card fc-risks-card">
          <h3>Risk factors</h3>
          <p>Issues that could pull your rating down if left unaddressed</p>
          <div className="fc-risks-list">
            {forecast.risk_factors.map((risk, i) => {
              const s = SEVERITY_STYLES[risk.severity] || SEVERITY_STYLES.low;
              return (
                <div key={i} className="fc-risk-item" style={{ borderLeftColor: s.color, background: s.bg }}>
                  <div className="fc-risk-header">
                    <span className="fc-risk-severity" style={{ color: s.color, borderColor: s.border }}>
                      {risk.severity}
                    </span>
                    <span className="fc-risk-title">{risk.title}</span>
                  </div>
                  <div className="fc-risk-desc">{risk.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="fc-footer">
        Generated {new Date(forecast.generated_at).toLocaleString()}
        {forecast.from_cache && ' (cached)'} · Based on {forecast.review_count} reviews
      </div>
    </div>
  );
}
